import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Panel, StatusBadge } from '@/components/kit';
import { ListBody } from '@/components/inputs';
import PaymentDialog from '@/components/admin/PaymentDialog';
import { PAYMENT_KIND_STYLES } from '@/shared/constants';
import { PAYMENT_FIELDS } from '@/shared/fields';
import { money, paymentLabel } from '@/shared/pricing';
import { dayOf, rows } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

/** Payments recorded against one order, with record and edit. */
export default function OrderPayments({ order }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, loading, error, run } = useFetch(
    () =>
      sdk
        .table('payments_c')
        .select(PAYMENT_FIELDS)
        .where(eq('order_c', order.Id))
        .orderByDesc('paid_on_c')
        .limit(50)
        .fetch()
        .then(rows),
    [order.Id],
  );

  const list = data ?? [];

  function edit(payment) {
    setEditing(payment);
    setEditOpen(true);
  }

  return (
    <Panel
      title="Payments"
      icon="Banknote"
      bodyClassName="py-1"
      actions={
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
          <ApperIcon name="Plus" size={16} />
          Record payment
        </Button>
      }
    >
      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        rows={2}
        isEmpty={!list.length}
        empty={{
          icon: 'Banknote',
          title: 'No payments recorded',
          description: 'Record money as it arrives so receipts and revenue add up.',
          action: () => setCreateOpen(true),
          actionLabel: 'Record payment',
        }}
      >
        <ul className="divide-y divide-border">
          {list.map((p) => (
            <li key={p.Id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <Link to={`/admin/payments/${p.Id}`} className="block truncate text-sm font-medium text-primary hover:underline">
                  {p.Name}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {p.paid_on_c ? formatLocalDate(dayOf(p.paid_on_c), DATE_FORMATS.SHORT) : 'No date'} · {paymentLabel(p.method_c)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge value={p.kind_c} styles={PAYMENT_KIND_STYLES} className="hidden sm:inline-flex" />
                <span className="tabular-nums">{money(p.amount_c)}</span>
                <Button variant="ghost" size="icon" aria-label={`Edit ${p.Name}`} onClick={() => edit(p)}>
                  <ApperIcon name="Pencil" size={16} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </ListBody>

      <PaymentDialog open={createOpen} onOpenChange={setCreateOpen} order={order} onSaved={run} />
      <PaymentDialog open={editOpen} onOpenChange={setEditOpen} payment={editing} order={order} onSaved={run} />
    </Panel>
  );
}
